import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CANALS } from '@/api/activacions';
import { useTipusAvisAdmin, type TipusAvisAdminDto } from '@/api/administracio';
import { useDesaTipusAvis, useEtags } from '@/api/ajustosAvisos';
import type { Canal } from '@/api/types';
import { Icona, type NomIcona } from '@/components/Icona';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Buit } from '@/pages/operacio/comu';
import { useMe } from '@/permisos/MeContext';
import { badgePerEstat } from '@/theme/theme';
import { CapPanell, ErrorDesa } from './comu';
import { BarraCanvis } from './marc/canvis';
import { esActiu, useEstructura } from './marc/estructura';
import './administracio.css';
import './avisos.css';

/** Canals que el backend assigna a un servei nou: els serveis que en tenen d'altres són excepcions. */
const CANALS_DEFECTE: Canal[] = ['PUSH', 'EMAIL'];

const commuta = (l: Canal[], c: Canal) => (l.includes(c) ? l.filter((x) => x !== c) : [...l, c]);
const mateixos = (a: Canal[], b: Canal[]) => a.length === b.length && a.every((c) => b.includes(c));
const badgeTipus = (codi: string) =>
  codi === 'CRITICA' ? 'badge--critic' : `badge--${badgePerEstat(codi)}`;
const canviat = (a: TipusAvisAdminDto, b: TipusAvisAdminDto) =>
  a.tempsConfirmacioSeg !== b.tempsConfirmacioSeg ||
  !mateixos(a.canalsObligatoris, b.canalsObligatoris);

function ReglaTemps({
  icona,
  titol,
  text,
  enllac,
}: {
  icona: NomIcona;
  titol: string;
  text: string;
  enllac?: { ruta: string; text: string };
}) {
  return (
    <div className="regla-temps">
      <span className="regla-temps__icona">
        <Icona nom={icona} />
      </span>
      <div>
        <div className="regla-temps__titol">{titol}</div>
        <p className="regla-temps__text">{text}</p>
        {enllac ? (
          <Link to={enllac.ruta} className="regla-temps__enllac">
            {enllac.text}
            <Icona nom="chevron-right" />
          </Link>
        ) : null}
      </div>
    </div>
  );
}

export function W26Canals() {
  const { t, i18n } = useTranslation('administracio');
  const tc = useTranslation('comu').t;
  const { pot } = useMe();
  const tipus = useTipusAvisAdmin();
  const estructura = useEstructura();
  const desa = useDesaTipusAvis();
  const editable = pot('cai.notificacions.politica', 'MODIFICAR');
  const llista = tipus.data ?? [];
  const etags = useEtags(
    '/tipus-avis',
    llista.map((x) => x.id),
  );
  const [esborranys, setEsborranys] = useState<Record<number, TipusAvisAdminDto>>({});
  const actual = (x: TipusAvisAdminDto) => esborranys[x.id] ?? x;
  const modificats = llista.filter((x) => esborranys[x.id] && canviat(esborranys[x.id]!, x));
  const nomTipus = (x: TipusAvisAdminDto) => x.traduccions?.[i18n.language]?.nom ?? x.nom;
  const canvia = (x: TipusAvisAdminDto, p: Partial<TipusAvisAdminDto>) =>
    setEsborranys((e) => ({ ...e, [x.id]: { ...actual(x), ...p } }));

  const excepcions = estructura.departaments.flatMap((d) =>
    d.arees.flatMap((a) =>
      a.serveis
        .filter((s) => esActiu(s) && !mateixos(s.canalsDefecte ?? CANALS_DEFECTE, CANALS_DEFECTE))
        .map((s) => ({ s, ruta: `${d.nom} › ${a.nom}` })),
    ),
  );
  const nomsDefecte = CANALS_DEFECTE.map((c) => tc(`canal.${c}`)).join(', ');

  return (
    <section className="pantalla" data-screen="W-26">
      <Toolbar titol={t('avisos.canals.titol')} />
      <div className="content stack">
        <div className="panel">
          <CapPanell titol={t('avisos.canals.reglesTemps')} icona="clock" />
          <div className="panel__body regles-temps">
            <ReglaTemps
              icona="bell"
              titol={t('avisos.canals.reglaConfirmacio')}
              text={t('avisos.canals.reglaConfirmacioText')}
            />
            <ReglaTemps
              icona="arrow-up"
              titol={t('avisos.canals.reglaCircuit')}
              text={t('avisos.canals.reglaCircuitText')}
              enllac={{ ruta: '/admin/circuits', text: t('avisos.canals.veureCircuits') }}
            />
            <ReglaTemps
              icona="gear"
              titol={t('avisos.canals.reglaDefecte')}
              text={t('avisos.canals.reglaDefecteText')}
              enllac={{ ruta: '/admin/configuracio', text: t('avisos.canals.veureConfiguracio') }}
            />
          </div>
          <ul className="regles-generals">
            <li>{t('canals.antiDuplicatsValor')}</li>
            <li>{t('canals.reintentsValor')}</li>
            <li>{t('canals.idiomaValor')}</li>
          </ul>
        </div>

        <div className="panel">
          <CapPanell titol={t('canals.matriu')} extra={t('canals.ajuda')} />
          {tipus.isError ? (
            <ErrorCarrega onReintenta={() => void tipus.refetch()} />
          ) : llista.length === 0 ? (
            <Buit icona="bell">
              {tipus.isLoading ? t('comu.carregant') : t('canals.senseTipus')}
            </Buit>
          ) : (
            <div className="taula-scroll">
              <table className="taula taula-canals">
                <thead>
                  <tr>
                    <th>{t('canals.tipusAvis')}</th>
                    {CANALS.map((c) => (
                      <th key={c} className="center">
                        {tc(`canal.${c}`)}
                      </th>
                    ))}
                    <th>{t('canals.tempsConfirmacio')}</th>
                  </tr>
                </thead>
                <tbody>
                  {llista.map((x) => {
                    const v = actual(x);
                    const nom = nomTipus(x);
                    const mod = modificats.includes(x);
                    return (
                      <tr key={x.id} data-tipus={x.codi} className={mod ? 'fila-modificada' : ''}>
                        <td>
                          <span className="hstack gap-6">
                            <span className={`badge ${badgeTipus(x.codi)}`}>{nom}</span>
                            {x.criticaPermesa ? (
                              <span title={t('canals.critica')}>
                                <Icona nom="exclamationmark-octagon" className="c-red" />
                              </span>
                            ) : null}
                            {x.actiu === false ? (
                              <span className="badge badge--gray">{t('comu.inactiu')}</span>
                            ) : null}
                          </span>
                        </td>
                        {CANALS.map((c) => (
                          <td key={c} className="center">
                            <input
                              type="checkbox"
                              className={`switch${c === 'PUSH_CRITICA' ? ' switch--critic' : ''}`}
                              aria-label={t('canals.canalDe', {
                                canal: tc(`canal.${c}`),
                                tipus: nom,
                              })}
                              checked={v.canalsObligatoris.includes(c)}
                              disabled={!editable}
                              onChange={() =>
                                canvia(x, { canalsObligatoris: commuta(v.canalsObligatoris, c) })
                              }
                            />
                          </td>
                        ))}
                        <td>
                          <span className="hstack gap-6">
                            <input
                              className="input input--curt"
                              type="number"
                              min={1}
                              aria-label={t('canals.tempsDe', { tipus: nom })}
                              value={Math.round(v.tempsConfirmacioSeg / 60)}
                              disabled={!editable}
                              onChange={(e) =>
                                canvia(x, {
                                  tempsConfirmacioSeg:
                                    Math.max(1, Number(e.target.value) || 1) * 60,
                                })
                              }
                            />
                            <span className="dim t-footnote">{t('canals.min')}</span>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {desa.isError ? (
            <div className="panel__body">
              <ErrorDesa error={desa.error} onRecarrega={() => void tipus.refetch()} />
            </div>
          ) : null}
        </div>

        <div className="panel">
          <CapPanell
            titol={t('avisos.canals.excepcions')}
            extra={t('avisos.canals.defecte', { canals: nomsDefecte })}
          />
          {estructura.error ? (
            <ErrorCarrega onReintenta={estructura.refetch} />
          ) : excepcions.length === 0 ? (
            <p className="panel__body t-footnote dim" style={{ margin: 0 }}>
              {estructura.carregant
                ? t('comu.carregant')
                : t('avisos.canals.senseExcepcions', { canals: nomsDefecte })}
            </p>
          ) : (
            <table className="taula">
              <thead>
                <tr>
                  <th>{t('avisos.canals.servei')}</th>
                  <th>{t('canals.canalsDefecte')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {excepcions.map(({ s, ruta }) => (
                  <tr key={s.id} data-servei={s.id}>
                    <td>
                      <div className="strong">{s.nom}</div>
                      <div className="t-caption dim">{ruta}</div>
                    </td>
                    <td>
                      <span className="chips">
                        {(s.canalsDefecte ?? []).length === 0 ? (
                          <span className="dim">—</span>
                        ) : (
                          (s.canalsDefecte ?? []).map((c) => (
                            <span key={c} className="badge badge--preventiu">
                              {tc(`canal.${c}`)}
                            </span>
                          ))
                        )}
                      </span>
                    </td>
                    <td className="num">
                      <Link
                        to={`/admin/arees?servei=${s.id}`}
                        className="btn btn--ghost btn--xs"
                        aria-label={t('avisos.canals.obreServei', { servei: s.nom })}
                      >
                        {t('avisos.canals.edita')}
                        <Icona nom="chevron-right" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {editable ? (
        <BarraCanvis
          n={modificats.length}
          desant={desa.isPending}
          onDescarta={() => setEsborranys({})}
          onDesa={() =>
            desa.mutate(
              modificats.map((x) => ({ tipus: actual(x), etag: etags.get(x.id) ?? null })),
              { onSuccess: () => setEsborranys({}) },
            )
          }
        />
      ) : null}
    </section>
  );
}
